import { Document } from "@/data/documents/Document";
import { DocumentList } from "@/data/documents/DocumentList";
import { IChecklist } from "@/interfaces/CheckLists";
import { Box, Stack, Tooltip } from "@mui/material";
import { Theme } from "klayowebshared";
import { isEmpty } from "lodash";
import { Component, CSSProperties } from "react";
import { withLocale, WithLocaleProps } from "../providers/LocaleProvider";
import AttachmentIcon from "../resources/images/icons-attachment.svg";
import { DocumentTable } from "./table/DocumentTable";

interface InlineDocumentListProps extends WithLocaleProps {
  documents?: DocumentList;
  filter?: string[];
  sx?: CSSProperties;
  onDocumentClick?: (e: any, doc: Document) => void;
  checklistFilter?: string[];
  checklists?: IChecklist[];
  onChecklistClick?: (checklist: IChecklist) => void;
  theme?: typeof Theme;
  size?: number;
}

class InlineDocumentListBase extends Component<InlineDocumentListProps> {
  static defaultProps = {
    size: 160
  };

  render() {
    const {
      documents,
      filter,
      sx,
      onDocumentClick,
      checklistFilter,
      checklists,
      onChecklistClick,
      theme
    } = this.props;

    return (
      <Stack
        direction={"column"}
        spacing={1}
        style={{ display: "flex", flexDirection: "column", ...sx }}
        color='text.primary'
        className='klayo_inlinedoclist'
      >
        <span>{this.props.translate("common.evidence")}:&nbsp;</span>
        <div>
          {!isEmpty(filter) && (
            <>
              <DocumentTable<Document>
                theme={theme}
                showHeader={false}
                pagination={false}
                showFooter={false}
                hideFirstLastBorder={false}
                dense={true}
                paper={false}
                rows={filter?.map((id: string) => {
                  const doc = documents?.getDocument(id);
                  return doc;
                })}
                sx={{
                  borderBottom: "1px solid var(--grey-gr-200)",
                  borderRadius: "0!important"
                }}
                rowHeight={48}
                columns={[
                  {
                    type: DocumentTable.columns.icon,
                    renderCell: () => <AttachmentIcon />
                  },
                  {
                    type: DocumentTable.columns.name,
                    valueGetter: (_value: any, row: any) => row.documentName,
                    renderCell: (params: any) => {
                      const doc = params.row;
                      return (
                        <>
                          {doc.isDeleted ? (
                            <Tooltip title={doc.documentName}>
                              <Stack direction={"row"} flexWrap={"wrap"} width={"100%"}>
                                <span className='klayo_inlinedoclist_deleted truncate'>
                                  {doc.documentName}
                                </span>
                                ({this.props.translate("status.deleted")})
                              </Stack>
                            </Tooltip>
                          ) : (
                            <Tooltip title={doc.documentName}>
                              <Box flex={1} className='truncate'>
                                {doc.documentName}
                              </Box>
                            </Tooltip>
                          )}
                        </>
                      );
                    }
                  }
                ]}
                getRowId={(row: any) => row.documentId}
                onDocumentSelect={(e: any, doc: any) => {
                  onDocumentClick?.(e, doc);
                }}
                rowHasSelect={(doc: any) => !doc.isDeleted && !doc.pendingDelete}
              />
            </>
          )}
          {!isEmpty(checklistFilter) && (
            <>
              <DocumentTable<IChecklist>
                theme={theme}
                showHeader={false}
                pagination={false}
                showFooter={false}
                hideFirstLastBorder={false}
                dense={true}
                paper={false}
                rows={
                  checklistFilter?.map((id) => {
                    const checklist = checklists?.find((c) => c.assignmentId === id);
                    return checklist;
                  }) as IChecklist[]
                }
                sx={{
                  borderBottom: "1px solid var(--grey-gr-200)",
                  borderRadius: "0!important"
                }}
                rowHeight={48}
                columns={[
                  {
                    type: DocumentTable.columns.icon,
                    renderCell: () => <AttachmentIcon />
                  },
                  {
                    type: DocumentTable.columns.name,
                    valueGetter: (_value, row) => row.name,
                    renderCell: (params) => {
                      const checklist = params.row;
                      return (
                        <>
                          {checklist.isAssignmentDeleted ? (
                            <Tooltip title={checklist.name}>
                              <Stack direction={"row"} flexWrap={"wrap"} width={"100%"}>
                                <span className='klayo_inlinedoclist_deleted truncate'>
                                  {checklist.name}
                                </span>
                                ({this.props.translate("status.deleted")})
                              </Stack>
                            </Tooltip>
                          ) : (
                            <Tooltip title={checklist.name}>
                              <Box flex={1} className='truncate'>
                                {checklist.name}
                              </Box>
                            </Tooltip>
                          )}
                        </>
                      );
                    }
                  }
                ]}
                getRowId={(row) => row.assignmentId}
                onDocumentSelect={(e, checklist) => {
                  onChecklistClick?.(checklist);
                }}
                rowHasSelect={(checklist) => !checklist.isAssignmentDeleted}
              />
            </>
          )}
        </div>
      </Stack>
    );
  }
}

export const InlineDocumentList = withLocale(InlineDocumentListBase);
